/**
 * platformErrors.js — operator copy for the PLATFORM error codes.
 *
 * Contract 1.8 published ~39 snake_case codes that were raised by routes this
 * app calls — sign-in, the carton library, dieline and artwork uploads,
 * approvals, workspace autosave — and published nowhere. A client meeting one
 * had no documented meaning to route by and fell into the generic branch.
 *
 * WHY A SECOND TABLE. `PREPRESS_ERROR_COPY` is the prepress surface and is
 * reached through `describePrepressError`, which returns null for every other
 * shape. Putting library and sign-in copy in a table named for prepress would
 * make the name a lie and the lookup confusing. Two tables, one obligation:
 * `contract.test.js` asserts copy exists for every code whose published
 * `audience` includes "designer", from EITHER table — so the split is a
 * readability choice and never a gap.
 *
 * Every entry is written from the contract's published `meaning` and
 * `client_action`, never from the code's name. That is the ⑭f rule: copy
 * invented from a name sent operators to the wrong control for a year.
 */

export const PLATFORM_ERROR_COPY = {
  /* ── sign-in ──────────────────────────────────────────────────────── */
  invalid_token: {
    title: 'Your session has expired',
    hint: 'Sign in again. Nothing you saved has been lost.',
  },
  invalid_refresh: {
    title: 'Your session could not be renewed',
    hint: 'Sign in again.',
  },
  reuse_detected: {
    // The contract says this is the signal a token was taken — so the copy
    // says "tell someone", not just "sign in again". Treating it as routine
    // is how a stolen session stays unreported.
    title: 'Your session was ended for safety',
    hint: 'A sign-in token was used twice, so every session was signed out. '
      + 'Sign in again — and tell an administrator if this keeps happening.',
  },
  not_provisioned: {
    title: 'This account has not been set up yet',
    hint: 'Accounts are created by an administrator. Ask yours to add your '
      + 'email address.',
  },
  email_conflict: {
    title: 'This email is already on another account',
    hint: 'An administrator needs to sort out which account is yours.',
  },
  inactive: {
    title: 'This account is switched off',
    hint: 'Ask an administrator to reactivate you or your organisation.',
  },
  sso_unconfigured: {
    title: 'Sign-in is not available on this server',
    hint: 'Nobody can sign in until it is configured. Please report it.',
  },
  forbidden: {
    title: 'You do not have access to that',
    hint: 'Your role does not allow it, or the item belongs to someone else. '
      + 'Trying again will not help.',
  },
  db_unconfigured: {
    title: 'The server has no database',
    hint: 'Nothing can be saved or loaded until that is fixed. Please report it.',
  },

  /* ── the carton library ───────────────────────────────────────────── */
  carton_not_found: {
    title: 'That carton is no longer here',
    hint: 'It was deleted, or it belongs to someone else and is private.',
  },
  // Saved jobs (contract 1.15, decided 2 Nov with #85).
  job_not_saveable: {
    title: 'This run can’t be saved',
    hint: 'Only a layout run that finished can be saved. Run the layout again, then save it.',
  },
  job_already_saved: {
    title: 'This run is already saved',
    hint: 'It is in the Jobs tray under Saved jobs — rename it there for a different name.',
  },
  saved_job_not_found: {
    title: 'That saved job is no longer here',
    hint: 'It was removed, or it isn’t one you can see. The list has been refreshed.',
  },
  // V2-AC-26 (#87). The Library shows its own dialog for this one (with the
  // matches and "Save anyway"); this copy is for anywhere else it surfaces.
  duplicate_carton: {
    title: 'Duplicate carton detected',
    hint: 'A carton with the same die and the same artwork is already in the '
      + 'library. Open the Library to see it, or save anyway from there.',
  },
  bad_visibility: {
    title: 'That sharing setting is not valid',
    hint: 'A carton is either private to you or shared with your organisation.',
  },
  bad_origin: {
    title: 'Something went wrong on our side',
    hint: 'The app sent a carton source the server does not recognise. '
      + 'Please report it.',
  },
  bad_age: {
    title: 'That filter is not valid',
    hint: 'Choose a number of days.',
  },
  no_source: {
    title: 'This carton has nothing stored to work from',
    hint: 'Upload its dieline again.',
  },
  no_die_file: {
    title: 'This carton’s die file is missing',
    hint: 'The record survived but the file did not. Upload the dieline again.',
  },
  empty_file: {
    title: 'That file is empty',
    hint: 'Check the file and upload it again.',
  },

  /* ── dielines and artwork ─────────────────────────────────────────── */
  die_unreadable: {
    title: 'That dieline could not be read',
    hint: 'Re-export it and try again.',
  },
  artwork_unreadable: {
    title: 'That artwork could not be read',
    hint: 'Re-export it and try again.',
  },
  empty_artwork: {
    title: 'That artwork has nothing in it',
    hint: 'Check the export settings — the file opened but contained nothing '
      + 'to place.',
  },
  registration_failed: {
    title: 'The artwork would not line up with the dieline',
    hint: 'Export both from the same origin and at the same scale, then try '
      + 'again.',
  },

  /* ── a die read from the artwork's panel borders (contract 1.10) ──── */
  // The server's message says WHERE (which shapes, which gap, which cartons),
  // so describeDieRefusal shows it under these titles rather than instead of
  // them.
  artwork_die_no_borders: {
    title: 'No die could be found in this artwork',
    hint: 'There is no DIELINE layer, no CutContour spot, and no outlined '
      + 'panels to read one from. Outline each panel and flap, or add a '
      + 'DIELINE layer, and upload again.',
  },
  artwork_die_not_tiled: {
    title: 'The outlined panels do not cover the whole carton',
    hint: 'Part of the carton has no outline, or the outlines overlap. Outline '
      + 'every panel and flap so they meet edge to edge, and upload again.',
  },
  artwork_die_ambiguous: {
    title: 'This file holds more than one carton',
    hint: 'Which one is meant cannot be read from the artwork. Export one '
      + 'carton per file, or add a DIELINE layer to the one you mean.',
  },
  artwork_die_bad_outline: {
    title: 'The panels do not join into one cut line',
    hint: 'They leave a gap or a hole, or meet only at a corner. Close the gap '
      + 'so neighbouring panel edges meet exactly, and upload again.',
  },

  /* ── blobs ────────────────────────────────────────────────────────── */
  blob_unconfigured: {
    title: 'The server has nowhere to store uploads',
    hint: 'Uploads cannot be kept until that is fixed. Please report it.',
  },
  blob_write_failed: {
    title: 'That file could not be saved',
    hint: 'Try once more. If it fails again, please report it.',
  },
  blob_read_failed: {
    title: 'That file could not be loaded',
    hint: 'Try once more. If it fails again, please report it.',
  },

  /* ── approvals ────────────────────────────────────────────────────── */
  job_not_found: {
    title: 'That run is no longer available',
    hint: 'Runs are kept for a limited time. Run the layout again.',
  },
  job_not_approvable: {
    title: 'That run cannot be submitted',
    hint: 'It did not finish successfully, so there is nothing to approve. '
      + 'Run it again.',
  },
  already_pending: {
    title: 'This run is already waiting for a decision',
    hint: 'An approver needs to decide on the existing submission first.',
  },
  already_decided: {
    title: 'This submission has already been decided',
    hint: 'Submit it again if it needs another look.',
  },
  approval_not_found: {
    title: 'That submission is no longer here',
    hint: 'Reload the page to see the current state.',
  },
  bad_decision: {
    title: 'Something went wrong on our side',
    hint: 'The app sent a decision the server does not recognise. Please '
      + 'report it.',
  },
  comment_required: {
    // The contract's reason, kept: a rejection with no reason sends the work
    // back with nothing to act on.
    title: 'A rejection needs a reason',
    hint: 'Say what is wrong, so whoever picks this up knows what to change.',
  },
  self_approval_forbidden: {
    title: 'You cannot approve your own run',
    hint: 'Ask another approver to look at it.',
  },

  /* ── workspace ────────────────────────────────────────────────────── */
  ui_state_too_large: {
    title: 'Something went wrong on our side',
    hint: 'The app tried to autosave more than the server accepts. Your work '
      + 'is unaffected — please report it.',
  },

  /* ── tenant logo ──────────────────────────────────────────────────── */
  no_logo: {
    // Not an error the operator caused or can fix: the app falls back to its
    // own wordmark. Copy exists because the contract publishes the code.
    title: 'No organisation logo is set',
    hint: 'The product wordmark is shown instead.',
  },
};

/** Copy for a platform code, or null. Null means "not ours" — the caller
 *  falls back to the server's own message, which is written to be readable. */
export function describePlatformError(code) {
  return (code && PLATFORM_ERROR_COPY[code]) || null;
}

/** The artwork-die codes (contract 1.10): the refusals die detection makes
 *  when it reads a die from an artwork's panel borders. */
export const ARTWORK_DIE_CODES = [
  'artwork_die_no_borders', 'artwork_die_not_tiled',
  'artwork_die_ambiguous', 'artwork_die_bad_outline',
];

/** The line to show when die detection refuses an upload with an artwork-die
 *  code: the code's title, then the server's reason — which names the shapes,
 *  the gap or the cartons it means. Null for any other body, so the caller's
 *  own message stands for everything this does not know. */
export function describeDieRefusal(body) {
  const d = body && body.detail;
  if (!d || typeof d !== 'object' || !ARTWORK_DIE_CODES.includes(d.code)) return null;
  const copy = describePlatformError(d.code);
  const reason = typeof d.message === 'string' && d.message ? d.message : copy.hint;
  return `${copy.title}. ${reason}`;
}
